'use client';

/**
 * CallNotice — the in-call notification family (Turn-12 design, 12b):
 * one shape, three temperatures, plus Turn 15's PATIENCE (15b). Amber
 * WARNING asks (title, subtitle, optional action button), green SUCCESS
 * reassures (single line, auto-dismisses on useCallMode's timer with the
 * hairline countdown bar), red ERROR blocks (title, subtitle, optional
 * text-link action), warm PATIENCE waits silently (title, subtitle, an
 * indeterminate sweep track, no icon/action/dismiss).
 *
 * Purely presentational — the notice payload and lifecycle (success
 * auto-clear, supersede-on-new-chain, PATIENCE/WARNING's Turn-15
 * release-exit) live in useCallMode; CallScreen decides placement (desktop:
 * warnings/patience above the controls, success over the transcript rail;
 * mobile: warnings above the controls, success/error dropping from the
 * top — MODEL_BUSY notices route through model-busy-ribbon.tsx there
 * instead, see call-screen.tsx and that file's header).
 *
 * All colors/sizes/radii are extracted verbatim from design-v2.html 12b
 * (Turn 15's PATIENCE branch: Claude Design Turn 15, 15b). Entry motion is
 * the design's toastIn (tailwind `call-notice-in`)/Turn 15's `patience-in`,
 * motion-safe-gated; the success hairline is `call-notice-dismiss` (3.4s,
 * matching the hook's timer); the Turn-15 recovery exit is `call-notice-
 * release` (see the `releasing` prop below).
 */

import {
  ArrowRight,
  Check,
  CircleAlert,
  Mic,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  CallNoticeKind,
  type CallNotice as CallNoticeData,
} from '@/types/chat';
import { cn } from '@/lib/utils';
import { PRESSABLE, V3_COLORS } from '@/lib/design-tokens';

export interface CallNoticeProps {
  notice: CallNoticeData;
  /** Renders the dismiss X on warning/error when provided. Success/patience
   *  have no X — success auto-fades on the hook's timer (design: "confirms,
   *  then fades"); patience is never dismissable at all (Turn 15, 15b). */
  onDismiss?: () => void;
  /** Mirrors CallControls' size prop — same responsive boundary. */
  size?: 'desktop' | 'mobile';
  /** Turn 15e "the exhale" — true while THIS notice is mid recovery-exit
   *  (useCallMode's `releasingNotice`, held for one `call-notice-release`
   *  duration past the state clear). Swaps the entrance animation for the
   *  release-downward one and disables the dismiss button (nothing to
   *  dismiss — it's already leaving). Only ever set for a MODEL_BUSY
   *  notice; every other retraction path stays instant, so this prop is a
   *  no-op (default false) everywhere else. */
  releasing?: boolean;
  className?: string;
}

/** Shared indeterminate sweep track (Turn 15b/15f) — the patience notice's
 *  bottom-edge signal: a 55%-wide highlight drifting across a flat track,
 *  deliberately NOT a countdown/progress bar (see call-notice.ts's
 *  patienceNotice() doc). `heightPx` differs between this file's desktop
 *  card (3px) and the mobile ribbon's slimmer strip (2.5px, see
 *  model-busy-ribbon.tsx) — everything else is identical, so both share
 *  this one implementation rather than duplicating the sweep markup.
 *  Reduced motion hides the moving highlight and leaves the flat track
 *  (15b: "sweep highlight hidden (static track)"). */
export function PatienceSweepTrack({ heightPx }: { heightPx: number }) {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 overflow-hidden"
      style={{ height: heightPx, backgroundColor: '#EFE7DA' }}
    >
      <span
        className="absolute inset-y-0 left-0 w-[55%] motion-safe:animate-patience-sweep motion-reduce:hidden"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(227,96,1,.55), transparent)',
        }}
      />
    </div>
  );
}

/** Per-temperature palette/shape values from design-v2.html 12b. Shared
 *  tones come from V3_COLORS; the rest are 12b-only tints with no existing
 *  token. */
const CARD_STYLES = {
  [CallNoticeKind.WARNING]: {
    bg: V3_COLORS.amberBg,
    border: V3_COLORS.amberBorder,
    shadow: {
      desktop: '0 20px 44px -18px rgba(154,117,52,.7)',
      mobile: '0 16px 34px -16px rgba(154,117,52,.6)',
    },
    tileBg: V3_COLORS.amberTile,
    iconColor: V3_COLORS.amber,
    title: V3_COLORS.amberText,
    subtitle: '#a98a55',
    dismiss: '#c2a875',
  },
  [CallNoticeKind.ERROR]: {
    bg: '#FBECEA',
    border: '#F3D3CE',
    shadow: {
      desktop: '0 16px 36px -20px rgba(217,72,59,.5)',
      mobile: '0 16px 34px -18px rgba(217,72,59,.5)',
    },
    tileBg: '#F6D8D3',
    iconColor: V3_COLORS.endRed,
    title: '#B23A2E',
    subtitle: '#c07a70',
    dismiss: '#d3a49c',
  },
} as const;

export function CallNotice({
  notice,
  onDismiss,
  size = 'mobile',
  releasing = false,
  className,
}: CallNoticeProps) {
  const isDesktop = size === 'desktop';

  if (notice.kind === CallNoticeKind.PATIENCE) {
    // Turn 15b — the calm WAITING treatment. In practice this only ever
    // renders at size="desktop": mobile routes MODEL_BUSY through the
    // slimmer model-busy-ribbon.tsx instead (see call-screen.tsx), so this
    // branch doesn't bother with a separate mobile geometry. NOT
    // dismissable — `onDismiss` is intentionally never wired up here,
    // matching SUCCESS's established "no X" precedent above. NEVER shows
    // an alert icon — that's the whole point of not being a WARNING.
    return (
      <div
        role="status"
        className={cn(
          'relative overflow-hidden rounded-[14px] border-[1px] border-[#E8E1D4] bg-[#F5F1EA] px-[14px] pb-[13px] pt-[12px]',
          releasing
            ? 'motion-safe:animate-call-notice-release motion-reduce:animate-call-notice-release-reduced'
            : 'motion-safe:animate-patience-in motion-reduce:animate-patience-in-reduced',
          className
        )}
        style={{ boxShadow: '0 16px 34px -22px rgba(49,54,78,.4)' }}
      >
        <div className="flex items-start gap-[11px]">
          {/* "Orb echo" — a static miniature of the voice orb's own
              gradient, not a live-driven element (this notice has no audio
              signal of its own to visualize). */}
          <span
            aria-hidden="true"
            className="mt-[1px] size-[30px] shrink-0 rounded-full opacity-[.9]"
            style={{
              background:
                'radial-gradient(circle at 50% 34%, #FFE8B6, #EC5F1C)',
            }}
          />
          <div className="min-w-0 flex-1 pb-[3px]">
            <div className="text-[13.5px] font-semibold leading-[1.4] text-[#31364E]">
              {notice.title}
            </div>
            {notice.subtitle && (
              <div className="mt-[2px] text-[12px] leading-[1.45] text-[#8a7c68]">
                {notice.subtitle}
              </div>
            )}
          </div>
        </div>
        <PatienceSweepTrack heightPx={3} />
      </div>
    );
  }

  if (notice.kind === CallNoticeKind.SUCCESS) {
    // Green pill: icon circle + single line + countdown hairline.
    return (
      <div
        role="status"
        className={cn(
          'relative inline-flex items-center overflow-hidden rounded-[13px] border-[1px] border-[#CFE6D8] bg-[#E8F3EC] motion-safe:animate-call-notice-in',
          isDesktop
            ? 'gap-[11px] py-[11px] pl-[12px] pr-[15px]'
            : 'gap-[9px] rounded-[12px] py-[9px] pl-[10px] pr-[13px]',
          className
        )}
        style={{
          boxShadow: isDesktop
            ? '0 16px 36px -18px rgba(31,138,91,.6)'
            : '0 14px 30px -16px rgba(31,138,91,.55)',
        }}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-[#D6ECDD]',
            isDesktop ? 'size-[28px]' : 'size-[24px]'
          )}
        >
          <Check
            strokeWidth={2.6}
            className={cn(
              'text-[#1F8A5B]',
              isDesktop ? 'size-[15px]' : 'size-[13px]'
            )}
          />
        </span>
        <span
          className={cn(
            'whitespace-nowrap font-semibold text-[#1F8A5B]',
            isDesktop ? 'text-[13.5px]' : 'text-[12.5px]'
          )}
        >
          {notice.title}
        </span>
        {/* Countdown hairline — scaleX 1→0 over the same 3.4s as the hook's
            auto-dismiss timer. opacity-[0.5] arbitrary (Bootstrap
            .opacity-50 collision). */}
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-0 h-[2.5px] w-full origin-left bg-[#1F8A5B] opacity-[0.5] motion-safe:animate-call-notice-dismiss"
        />
      </div>
    );
  }

  const s = CARD_STYLES[notice.kind];
  const AlertIcon =
    notice.kind === CallNoticeKind.WARNING ? TriangleAlert : CircleAlert;

  return (
    <div
      role="status"
      className={cn(
        'flex items-start rounded-[14px] border-[1px]',
        releasing
          ? 'motion-safe:animate-call-notice-release motion-reduce:animate-call-notice-release-reduced'
          : 'motion-safe:animate-call-notice-in',
        isDesktop
          ? 'gap-[12px] px-[14px] py-[13px]'
          : 'gap-[11px] px-[13px] py-[12px]',
        className
      )}
      style={{
        backgroundColor: s.bg,
        borderColor: s.border,
        boxShadow: s.shadow[size],
      }}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center',
          isDesktop ? 'size-[30px] rounded-[9px]' : 'size-[28px] rounded-[8px]'
        )}
        style={{ backgroundColor: s.tileBg }}
      >
        <AlertIcon
          strokeWidth={2}
          className={isDesktop ? 'size-[16px]' : 'size-[15px]'}
          style={{ color: s.iconColor }}
        />
      </span>

      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'font-semibold leading-[1.4]',
            isDesktop ? 'text-[13.5px]' : 'text-[12.5px]'
          )}
          style={{ color: s.title }}
        >
          {notice.title}
        </div>
        {notice.subtitle && (
          <div
            className={cn(
              'mt-[2px] leading-[1.45]',
              isDesktop ? 'text-[12px]' : 'text-[11.5px]'
            )}
            style={{ color: s.subtitle }}
          >
            {notice.subtitle}
          </div>
        )}
        {notice.action &&
          (notice.kind === CallNoticeKind.WARNING ? (
            // Warning action: filled amber button with the mic glyph.
            <button
              type="button"
              onClick={notice.action.onClick}
              className={cn(
                'inline-flex items-center bg-[#E8A94E] font-semibold text-[#5f4718]',
                PRESSABLE,
                isDesktop
                  ? 'mt-[10px] h-[30px] gap-[6px] rounded-[9px] px-[13px] text-[12.5px]'
                  : 'mt-[8px] h-[28px] gap-[5px] rounded-[8px] px-[12px] text-[12px]'
              )}
            >
              <Mic
                strokeWidth={2.2}
                className={isDesktop ? 'size-[13px]' : 'size-[12px]'}
              />
              {notice.action.label}
            </button>
          ) : (
            // Error action: red text link with the arrow glyph.
            <button
              type="button"
              onClick={notice.action.onClick}
              className={cn(
                'inline-flex items-center font-semibold text-[#D9483B]',
                PRESSABLE,
                isDesktop
                  ? 'mt-[9px] gap-[5px] text-[12.5px]'
                  : 'mt-[8px] gap-[5px] text-[12px]'
              )}
            >
              {notice.action.label}
              <ArrowRight
                strokeWidth={2}
                className={isDesktop ? 'size-[13px]' : 'size-[12px]'}
              />
            </button>
          ))}
      </div>

      {/* Nothing to dismiss mid release-exit (Turn 15e) — it's already
          leaving on its own. */}
      {onDismiss && !releasing && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className={cn(
            'flex size-[22px] shrink-0 items-center justify-center',
            PRESSABLE
          )}
          style={{ color: s.dismiss }}
        >
          <X strokeWidth={2} className="size-[13px]" />
        </button>
      )}
    </div>
  );
}
